import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import AppFooter from './AppFooter';
import { Menu, Moon, Search, Settings, Sun, User as UserIcon, LogOut, KeyRound, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth, PROXY_CANCELLED } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import ProxySessionBar from './ProxySessionBar';
import LocationSwitcher from './LocationSwitcher';
import { authAPI, workspaceAdminAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { getHomeRouteByRole } from '../lib/rbac';
import { isOperatorConsole as isOperatorConsoleView, shouldLoadProxyTargets, subTenantOptions } from '../lib/proxySwitcher';

const Layout = ({ children, items, activeItem, onNavigate, onLogout }) => {
  const navigate = useNavigate();
  const { user, proxyLogin, exitProxy } = useAuth();
  const { hidePlatformBranding } = useBranding();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [proxyTargets, setProxyTargets] = useState({ items: [], scope: null, source_tenant_id: null });
  const [proxyTargetsLoading, setProxyTargetsLoading] = useState(false);
  const [proxySwitching, setProxySwitching] = useState(false);
  const [superadminTenantSelection, setSuperadminTenantSelection] = useState('');
  const [superadminSubTenantSelection, setSuperadminSubTenantSelection] = useState('');
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem('hrms_theme');
      if (stored === 'dark' || stored === 'light') return stored;
    } catch (error) {
    }
    const prefersDark = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
    return prefersDark ? 'dark' : 'light';
  });

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    const nextTheme = theme === 'dark' ? 'dark' : 'light';
    document.documentElement.classList.toggle('dark', nextTheme === 'dark');
    try {
      localStorage.setItem('hrms_theme', nextTheme);
    } catch (error) {
    }
  }, [theme]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  const displayName = useMemo(() => {
    if (user?.isProxySession) {
      const proxyTenantName = String(user?.tenantName || user?.tenantSlug || '').trim();
      if (proxyTenantName) return proxyTenantName;
    }
    const explicitName = String(user?.name || '').trim();
    if (explicitName) return explicitName;
    const email = String(user?.email || '').trim();
    if (!email) return 'User';
    const beforeAt = email.split('@')[0] || email;
    return beforeAt;
  }, [user?.email, user?.isProxySession, user?.name, user?.tenantName, user?.tenantSlug]);

  const profileMeta = useMemo(() => {
    if (user?.isProxySession) {
      const parentName = String(user?.parentTenantName || user?.parentTenantSlug || '').trim();
      const currentName = String(user?.tenantName || user?.tenantSlug || '').trim();
      if (parentName && currentName && parentName !== currentName) {
        return `${parentName} / ${currentName}`;
      }
      if (currentName) return currentName;
      if (parentName) return parentName;
      return 'Proxy Session';
    }
    return String(user?.email || '').trim();
  }, [
    user?.email,
    user?.isProxySession,
    user?.parentTenantName,
    user?.parentTenantSlug,
    user?.tenantName,
    user?.tenantSlug,
  ]);

  const avatarFallback = useMemo(() => {
    const source = String(displayName || '').trim();
    if (!source) return 'U';
    const letters = source.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2);
    return letters ? letters.toUpperCase() : 'U';
  }, [displayName]);

  const trialBanner = useMemo(() => {
    const limits = user?.limits || {};
    const trialActive = Boolean(limits?.trial_active);
    if (!trialActive || user?.isSuperadmin) return null;
    const remainingDays = Number(limits?.trial_remaining_days || 0);
    const planName = String(user?.subscriptionPlanName || user?.subscriptionPlanCode || 'Current').trim();
    const tenantName = String(user?.tenantName || user?.tenantSlug || 'workspace').trim();
    return {
      title: `${planName} trial is active`,
      message: remainingDays > 0
        ? `${remainingDays} day${remainingDays === 1 ? '' : 's'} remaining for ${tenantName}.`
        : `Your ${planName} trial is active for ${tenantName}.`,
      remainingDays,
      tenantName,
      planName,
    };
  }, [user?.isSuperadmin, user?.limits, user?.subscriptionPlanCode, user?.subscriptionPlanName, user?.tenantName, user?.tenantSlug]);

  const handleUpgradePlanCta = useCallback(() => {
    if (!trialBanner) return;
    const subject = encodeURIComponent(`Upgrade Plan Request - ${trialBanner.tenantName}`);
    const body = encodeURIComponent(
      `Hello Superadmin,\n\nPlease upgrade the current subscription plan for ${trialBanner.tenantName}.\nCurrent plan: ${trialBanner.planName}\nTrial remaining: ${trialBanner.remainingDays} day(s)\n\nThanks.`
    );
    window.location.href = `mailto:superadmin@webhr.in?subject=${subject}&body=${body}`;
  }, [trialBanner]);

  const flatNavItems = useMemo(() => {
    const nav = Array.isArray(items) ? items : [];
    return nav.flatMap((item) => {
      const base = item ? [{ id: item.id, label: item.label, group: null }] : [];
      const subs = Array.isArray(item?.subItems)
        ? item.subItems.map((sub) => ({ id: sub.id, label: sub.label, group: item.label }))
        : [];
      return [...base, ...subs].filter((row) => row.id && row.label);
    });
  }, [items]);

  const findNavItem = useCallback(
    (id) => flatNavItems.find((row) => row.id === id) || null,
    [flatNavItems]
  );

  const goToTab = useCallback(
    (tabId) => {
      if (typeof onNavigate === 'function' && findNavItem(tabId)) {
        onNavigate(tabId);
        return;
      }
      const home = getHomeRouteByRole(user?.role);
      navigate(home);
    },
    [findNavItem, navigate, onNavigate, user?.role]
  );

  const handleDashboardClick = useCallback(() => {
    if (findNavItem('dashboard')) return goToTab('dashboard');
    if (findNavItem('overview')) return goToTab('overview');
    const nav = Array.isArray(items) ? items : [];
    if (nav[0]?.id) return goToTab(nav[0].id);
    navigate(getHomeRouteByRole(user?.role));
  }, [findNavItem, goToTab, items, navigate, user?.role]);

  const handleProfileClick = useCallback(() => {
    if (findNavItem('profile')) return goToTab('profile');
    navigate(getHomeRouteByRole(user?.role));
  }, [findNavItem, goToTab, navigate, user?.role]);

  const handleSettingsClick = useCallback(() => {
    if (findNavItem('system')) return goToTab('system');
    if (findNavItem('workspace')) return goToTab('workspace');
    navigate(getHomeRouteByRole(user?.role));
  }, [findNavItem, goToTab, navigate, user?.role]);

  const handleResetPassword = useCallback(async () => {
    const email = String(user?.email || '').trim();
    if (!email) return;
    try {
      await authAPI.requestPasswordReset(email);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to request password reset'));
    }
  }, [user?.email]);

  const shouldShowProxySwitcher = useMemo(
    () => shouldLoadProxyTargets(user),
    [user]
  );

  useEffect(() => {
    let mounted = true;
    const loadProxyTargets = async () => {
      if (!shouldShowProxySwitcher) {
        setProxyTargets({ items: [], scope: null, source_tenant_id: null });
        return;
      }
      try {
        setProxyTargetsLoading(true);
        let response = await workspaceAdminAPI.listProxyTargets();
        let items = Array.isArray(response?.data?.items) ? response.data.items : [];
        let scope = response?.data?.scope || null;
        let sourceTenantId = response?.data?.source_tenant_id || null;

        if (user?.isSuperadmin && items.length === 0) {
          const tenantsResponse = await workspaceAdminAPI.listTenants();
          const tenantRows = Array.isArray(tenantsResponse?.data) ? tenantsResponse.data : [];
          items = tenantRows.map((tenant) => ({
            id: tenant.id,
            slug: tenant.slug,
            name: tenant.name,
            parent_tenant_id: tenant.parent_tenant_id ?? null,
            tenant_level: tenant.tenant_level ?? (tenant.parent_tenant_id ? 2 : 1),
          }));
          scope = 'superadmin';
        }

        if (!mounted) return;
        setProxyTargets({
          items,
          scope,
          source_tenant_id: sourceTenantId,
        });
      } catch (error) {
        if (!mounted) return;
        if (user?.isSuperadmin) {
          try {
            const tenantsResponse = await workspaceAdminAPI.listTenants();
            const tenantRows = Array.isArray(tenantsResponse?.data) ? tenantsResponse.data : [];
            setProxyTargets({
              items: tenantRows.map((tenant) => ({
                id: tenant.id,
                slug: tenant.slug,
                name: tenant.name,
                parent_tenant_id: tenant.parent_tenant_id ?? null,
                tenant_level: tenant.tenant_level ?? (tenant.parent_tenant_id ? 2 : 1),
              })),
              scope: 'superadmin',
              source_tenant_id: null,
            });
          } catch (fallbackError) {
            setProxyTargets({ items: [], scope: null, source_tenant_id: null });
          }
        } else {
          setProxyTargets({ items: [], scope: null, source_tenant_id: null });
        }
      } finally {
        if (mounted) setProxyTargetsLoading(false);
      }
    };

    loadProxyTargets();
    return () => {
      mounted = false;
    };
  }, [shouldShowProxySwitcher, user?.tenantId, user?.tenantSlug, user?.canReturnToSuperadmin, user?.isSuperadmin]);

  const proxyTargetItems = useMemo(
    () => (Array.isArray(proxyTargets?.items) ? proxyTargets.items : []),
    [proxyTargets?.items]
  );

  const isOperatorConsole = useMemo(
    () => isOperatorConsoleView(user, proxyTargets?.scope),
    [proxyTargets?.scope, user]
  );

  const proxyTargetMap = useMemo(
    () => new Map(proxyTargetItems.map((row) => [String(row.slug), row])),
    [proxyTargetItems]
  );

  const rootTenantOptions = useMemo(
    () => proxyTargetItems.filter((row) => !row.parent_tenant_id),
    [proxyTargetItems]
  );

  const currentTenantRecord = useMemo(
    () => proxyTargetMap.get(String(user?.tenantSlug || '')) || null,
    [proxyTargetMap, user?.tenantSlug]
  );

  useEffect(() => {
    if (!isOperatorConsole) {
      setSuperadminTenantSelection('');
      setSuperadminSubTenantSelection('');
      return;
    }
    // Outside a proxy session there is no tenant to mirror, so leave whatever
    // the operator has picked alone. Clearing it here reset the parent select on
    // every render pass and the sub-tenant select — gated on that value — never
    // opened.
    if (!user?.isProxySession) return;
    if (currentTenantRecord?.parent_tenant_id) {
      const parentRecord = proxyTargetItems.find((row) => Number(row.id) === Number(currentTenantRecord.parent_tenant_id));
      setSuperadminTenantSelection(parentRecord?.slug || '');
      setSuperadminSubTenantSelection(currentTenantRecord.slug || '');
      return;
    }
    setSuperadminTenantSelection(currentTenantRecord?.slug || '');
    setSuperadminSubTenantSelection('');
  }, [currentTenantRecord, isOperatorConsole, proxyTargetItems, user?.isProxySession]);

  const superadminSelectedTenantRecord = useMemo(
    () => proxyTargetMap.get(String(superadminTenantSelection || '')) || null,
    [proxyTargetMap, superadminTenantSelection]
  );

  const superadminSubTenantOptions = useMemo(
    () => subTenantOptions(proxyTargetItems, superadminSelectedTenantRecord),
    [proxyTargetItems, superadminSelectedTenantRecord]
  );

  const navigateAfterProxySwitch = useCallback((responseData = null) => {
    navigate(getHomeRouteByRole(responseData?.role || user?.role));
  }, [navigate, user?.role]);

  const handleTenantProxySwitch = useCallback(async (value) => {
    const nextValue = String(value || '');
    if (!nextValue) return;
    try {
      if (nextValue === '__view_all__') {
        setSuperadminTenantSelection('');
        setSuperadminSubTenantSelection('');
        setProxySwitching(true);
        if (user?.isProxySession) {
          const response = await exitProxy();
          navigateAfterProxySwitch(response);
        } else {
          navigate(user?.isSuperadmin ? '/superadmin' : getHomeRouteByRole(user?.role));
        }
        return;
      }
      setSuperadminTenantSelection(nextValue);
      setSuperadminSubTenantSelection('');
      setProxySwitching(true);
      const response = await proxyLogin(nextValue);
      navigateAfterProxySwitch(response);
      toast.success('Tenant view updated');
    } catch (error) {
      if (error?.code === PROXY_CANCELLED) return;
      toast.error(getErrorMessage(error, 'Failed to switch tenant view'));
    } finally {
      setProxySwitching(false);
    }
  }, [exitProxy, navigate, navigateAfterProxySwitch, proxyLogin, user?.isProxySession, user?.isSuperadmin, user?.role]);

  const handleSubTenantProxySwitch = useCallback(async (value) => {
    const nextValue = String(value || '');
    if (!nextValue) return;
    try {
      setSuperadminSubTenantSelection(nextValue === '__view_all__' ? '' : nextValue);
      if (nextValue === '__view_all__') {
        if (!superadminTenantSelection) return;
        setProxySwitching(true);
        const response = await proxyLogin(superadminTenantSelection);
        navigateAfterProxySwitch(response);
        toast.success('Tenant view updated');
        return;
      }
      setProxySwitching(true);
      const response = await proxyLogin(nextValue);
      navigateAfterProxySwitch(response);
      toast.success('Tenant view updated');
    } catch (error) {
      if (error?.code === PROXY_CANCELLED) return;
      toast.error(getErrorMessage(error, 'Failed to switch sub-tenant view'));
    } finally {
      setProxySwitching(false);
    }
  }, [navigateAfterProxySwitch, proxyLogin, superadminTenantSelection]);

  const searchResults = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return [];
    return flatNavItems
      .filter((row) => String(row.label || '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [flatNavItems, searchQuery]);

  return (
    // Column so the proxy banner spans the full width above both the sidebar
    // and the content — it is a statement about the whole session, not about
    // one pane of it.
    <div className="role-shell flex h-screen flex-col overflow-hidden">
      <ProxySessionBar />
      <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Sidebar */}
      <div className="shrink-0 z-40 md:z-auto">
        <Sidebar 
          items={items}
          activeItem={activeItem}
          onNavigate={onNavigate}
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          onLogout={onLogout}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="bg-background/90 backdrop-blur-md border-b border-border/80 h-16 flex items-center justify-between px-3 md:px-4 shrink-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                Welcome, {displayName}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {greeting}{profileMeta ? ` • ${profileMeta}` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {shouldShowProxySwitcher ? (
              isOperatorConsole ? (
                <>
                <Select value={superadminTenantSelection || undefined} onValueChange={handleTenantProxySwitch} disabled={proxyTargetsLoading || proxySwitching}>
                  <SelectTrigger className="h-9 w-[140px] sm:w-[180px] md:w-[220px]">
                    <SelectValue placeholder={proxyTargetsLoading ? 'Loading tenants...' : 'Tenant'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__view_all__">View All</SelectItem>
                    {rootTenantOptions.map((row) => (
                      <SelectItem key={`tenant-proxy-${row.id}`} value={row.slug}>
                        {row.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={superadminSubTenantSelection || undefined}
                  onValueChange={handleSubTenantProxySwitch}
                  disabled={proxyTargetsLoading || proxySwitching}
                >
                  <SelectTrigger className="h-9 w-[150px] sm:w-[190px] md:w-[220px]">
                    <SelectValue placeholder="Sub-Tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__view_all__">View All</SelectItem>
                    {superadminSubTenantOptions.map((row) => (
                      <SelectItem key={`subtenant-proxy-${row.id}`} value={row.slug}>
                        {row.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </>
              ) : null
            ) : null}

            {/* Locations of this organisation. Self-contained and server-gated,
                so it replaces the old "Sub-Tenant" select rather than sitting
                beside it: that one was driven by listProxyTargets, which only
                ever answered for a ROOT tenant's admin — a branch admin saw no
                switcher at all, which is the gap this closes. Superadmins keep
                their own two selects above; entering a customer's workspace is
                a different act and keeps its consent dialog. */}
            {!isOperatorConsole ? <LocationSwitcher /> : null}

            <button
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex items-center gap-2 h-9 rounded-lg border border-border bg-muted/60 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:border-primary/30 hover:text-foreground"
            >
              <Search className="h-4 w-4" />
              <span>Search…</span>
            </button>
            <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => setSearchOpen(true)}>
              <Search className="h-5 w-5" />
            </Button>

            <Button variant="ghost" size="icon" onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 px-2 gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profilePhotoUrl || ''} alt={displayName} />
                    <AvatarFallback>{avatarFallback}</AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm font-medium max-w-[160px] truncate">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profilePhotoUrl || ''} alt={displayName} />
                    <AvatarFallback>{avatarFallback}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">{profileMeta}</div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDashboardClick}>
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleProfileClick}>
                  <UserIcon className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleResetPassword}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Reset Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSettingsClick}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => (typeof onLogout === 'function' ? onLogout() : undefined)}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Scrollable Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-5 lg:p-6 scroll-smooth relative w-full">
          <div className="role-content max-w-[1400px] mx-auto p-4 md:p-5 lg:p-6 space-y-6 w-full animate-fade-in">
            {trialBanner ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">{trialBanner.title}</p>
                    <p className="mt-1 text-sm text-amber-800">{trialBanner.message}</p>
                  </div>
                  <Button className="shrink-0" onClick={handleUpgradePlanCta}>
                    Upgrade Plan
                  </Button>
                </div>
              </div>
            ) : null}
            {children}
            <AppFooter className="border-t border-border/70 pt-4" />
          </div>
        </main>
      </div>

      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Search</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search menu..." />
            <div className="max-h-[320px] overflow-y-auto rounded-md border border-border">
              {searchResults.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">No results</div>
              ) : (
                <div className="p-1">
                  {searchResults.map((row) => (
                    <button
                      key={`search-${row.id}`}
                      type="button"
                      className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => {
                        setSearchOpen(false);
                        setSearchQuery('');
                        goToTab(row.id);
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate">{row.label}</span>
                        {row.group ? <span className="text-xs text-muted-foreground truncate">{row.group}</span> : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>

      {/* The only place the product names itself inside a tenant's workspace, so
          hiding it is what "white label" actually means here. Pinned to the
          viewport rather than sitting in the sidebar: it belongs to the product,
          not to the navigation, and the collapsed rail had nowhere to put it.

          `pointer-events-none` so it can never sit over a control and eat a
          click — it is a mark, not a target. */}
      {!hidePlatformBranding && (
        <span className="pointer-events-none fixed bottom-2 right-3 z-40 select-none text-[10px] tracking-wide text-muted-foreground/70">
          Powered by Appable
        </span>
      )}
    </div>
  );
};

export default Layout;
